// @ts-nocheck
/**
 * One receipt screen for both paid-extras and paid scheduling requests.
 *
 * Stripe redirects the instant the card clears, so the webhook may still be in
 * flight — NOT_READY is polled, never reported as "not found". Prices from the
 * RPC are already dollars; nothing here divides by 100.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, CalendarDays, MapPin, Clock } from "lucide-react";
import { formatCalendarRange, formatCalendarDate, formatTimeRange, formatTime } from "@/lib/dates";
import { goToRecruitLook } from "@/lib/recruitlookHandoff";
import { getRlTeams } from "@/lib/rlTeams";
import { CONSTRAINT_TYPE_LABELS } from "@/lib/constraints/humanize";

const POLL_MS = 1500;
const POLL_LIMIT_MS = 20000;

const money = (v: any) =>
  `$${Number(v ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatPaidAt = (iso?: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const PurchaseReceiptPage: React.FC = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  // Media credential checkouts arrive tagged, because payment is the approval
  // and the receipt is assembled from the checkout rather than a webhook row.
  const isMedia = params.get("kind") === "media";
  const mediaAmountCents = Number(params.get("amount") || "0") || 0;
  const mediaEventId = params.get("event_id") || "";

  const [receipt, setReceipt] = useState<any>(null);
  const [state, setState] = useState<"loading" | "found" | "pending" | "no_session">(
    sessionId ? "loading" : "no_session"
  );
  const [teamName, setTeamName] = useState<string | null>(null);
  const [goingToDashboard, setGoingToDashboard] = useState(false);
  const stopped = useRef(false);

  const goDashboard = useCallback(async () => {
    setGoingToDashboard(true);
    await goToRecruitLook();
  }, []);

  // Media receipt: no polling. The payment is done and it is the approval.
  useEffect(() => {
    if (!isMedia || !sessionId) return;
    let cancelled = false;
    (async () => {
      let event = null;
      if (mediaEventId) {
        const { data } = await (supabase as any)
          .from("events")
          .select("id, name, start_date, end_date, venue_name, venue_city, venue_state")
          .eq("id", mediaEventId)
          .maybeSingle();
        event = data ?? null;
      }
      if (cancelled) return;
      setReceipt({
        ok: true,
        kind: "media",
        total: mediaAmountCents / 100,
        items: [{ name: "Media credential", price: mediaAmountCents / 100 }],
        event,
      });
      setState("found");
    })();
    return () => {
      cancelled = true;
    };
  }, [isMedia, sessionId, mediaEventId, mediaAmountCents]);

  useEffect(() => {
    if (!sessionId || isMedia) return;
    stopped.current = false;
    const started = Date.now();

    const tick = async () => {
      if (stopped.current) return;
      try {
        const { data } = await (supabase as any).rpc("rle_get_purchase_receipt", {
          p_session_id: sessionId,
        });
        if (stopped.current) return;
        if (data?.ok) {
          setReceipt(data);
          setState("found");
          return;
        }
        if (data?.code === "NO_SESSION") {
          setState("no_session");
          return;
        }
      } catch {
        // fall through to retry — a transient failure must not show "not found"
      }
      if (Date.now() - started >= POLL_LIMIT_MS) {
        setState("pending");
        return;
      }
      setTimeout(tick, POLL_MS);
    };

    tick();
    return () => {
      stopped.current = true;
    };
  }, [sessionId, isMedia]);


  // Scheduling receipts carry only a RecruitLook team id. A slow lookup must
  // never block the receipt, so this resolves in the background.
  useEffect(() => {
    const id = receipt?.kind === "scheduling" ? receipt?.team_id : null;
    if (!id) return;
    let cancelled = false;
    getRlTeams([String(id)])
      .then((map) => {
        const t = map[String(id)];
        if (!cancelled && t) setTeamName(t.display_name || t.name || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [receipt]);

  if (state === "no_session") {
    return (
      <Shell>
        <Card className="w-full max-w-lg">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <h1 className="text-xl font-bold">No purchase to show</h1>
            <p className="text-muted-foreground">
              We don't have a payment reference for this page.
            </p>
            <DashboardButton onClick={goDashboard} loading={goingToDashboard} />
          </CardContent>
        </Card>
      </Shell>
    );
  }

  if (state === "loading") {
    return (
      <Shell>
        <Card className="w-full max-w-lg">
          <CardContent className="pt-10 pb-10 text-center">
            <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Confirming your payment…</p>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  if (state === "pending") {
    return (
      <Shell>
        <Card className="w-full max-w-lg">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto" />
            <h1 className="text-2xl font-bold">Your payment went through</h1>
            <p className="text-muted-foreground">
              We're still confirming the details — this usually takes a moment. Your receipt will
              arrive by email, and this page will show it once it's ready.
            </p>
            <DashboardButton onClick={goDashboard} loading={goingToDashboard} />
          </CardContent>
        </Card>
      </Shell>
    );
  }

  const isScheduling = receipt?.kind === "scheduling";
  const event = receipt?.event || null;
  const paidAt = formatPaidAt(receipt?.paid_at);
  const displayTeam = isScheduling ? teamName : receipt?.team_name;
  const division = receipt?.division;
  // total is what Stripe charged; items_total and processing_fee are the split.
  const processingFee = Number(receipt?.processing_fee ?? 0) || 0;
  const itemsTotal =
    receipt?.items_total != null
      ? Number(receipt.items_total)
      : Math.max(0, (Number(receipt?.total) || 0) - processingFee);

  const p = receipt?.parameters || {};
  const requestLabel =
    CONSTRAINT_TYPE_LABELS[receipt?.constraint_type] ||
    String(receipt?.constraint_type || "").replace(/_/g, " ");

  return (
    <Shell>
      <Card className="w-full max-w-xl">
        <CardContent className="pt-8 pb-8 space-y-6">
          {/* Paid */}
          <div className="text-center space-y-2">
            <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto" />
            <h1 className="text-2xl font-bold">Paid</h1>
            <p className="text-3xl font-bold text-primary">{money(receipt?.total)}</p>
            {paidAt && <p className="text-sm text-muted-foreground">{paidAt}</p>}
            {receipt?.buyer_email && (
              <p className="text-sm text-muted-foreground">
                Receipt sent to {receipt.buyer_email}
              </p>
            )}
          </div>

          {receipt?.kind === "media" && (
            <div className="rounded-lg border border-green-600/40 bg-green-600/10 p-4 text-sm">
              Your media credential is approved. Bring photo ID to the media table at check-in.
            </div>
          )}

          {isScheduling && (

            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
              Your request has been sent to the event organiser and is awaiting review. You'll hear
              back by email.
            </div>
          )}

          {/* What was bought */}
          <div className="space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              What you bought
            </h2>
            <div className="rounded-lg border divide-y">
              {isScheduling ? (
                <div className="p-4 space-y-1">
                  <div className="flex items-start justify-between gap-4">
                    <span className="font-medium">{requestLabel || "Scheduling request"}</span>
                    <span className="font-semibold">{money(itemsTotal)}</span>

                  </div>
                  {(p.blackout_date || p.date) && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <CalendarDays className="w-3.5 h-3.5" />
                      {formatCalendarDate(p.blackout_date || p.date)}
                    </p>
                  )}
                  {(p.start_time || p.end_time || p.time) && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {p.start_time && p.end_time
                        ? formatTimeRange(p.start_time, p.end_time)
                        : formatTime(p.start_time || p.end_time || p.time, "")}
                    </p>
                  )}
                  {p.notes && <p className="text-sm text-muted-foreground">“{p.notes}”</p>}
                </div>
              ) : (
                (receipt?.items || []).map((item: any, i: number) => (
                  <div key={item.id ?? i} className="p-4 space-y-1">
                    <div className="flex items-start justify-between gap-4">
                      <span className="font-medium">{item.name}</span>
                      <span className="font-semibold">{money(item.price)}</span>
                    </div>
                    {item.player_name && (
                      <p className="text-sm text-muted-foreground">For {item.player_name}</p>
                    )}
                    {(item.game_date || item.game_label) && (
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <CalendarDays className="w-3.5 h-3.5" />
                        {[item.game_date ? formatCalendarDate(item.game_date) : null, item.game_label]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Items / card fee / charged total. The fee row is absent when
                there was no card fee — a $0.00 line only raises questions. */}
            <div className="rounded-lg border p-4 space-y-1.5 text-sm">
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Items</span>
                <span>{money(itemsTotal)}</span>
              </div>
              {processingFee > 0 && (
                <div className="flex items-center justify-between gap-4">
                  <span className="text-muted-foreground">Card processing fee (3%)</span>
                  <span>{money(processingFee)}</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-4 border-t pt-1.5 font-semibold">
                <span>Paid</span>
                <span>{money(receipt?.total)}</span>
              </div>
            </div>
          </div>


          {/* Who it's for */}
          {(displayTeam || division || event) && (
            <div className="space-y-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Who it's for
              </h2>
              <div className="rounded-lg border p-4 space-y-1">
                {(displayTeam || division) && (
                  <p className="font-medium">
                    {[displayTeam, division].filter(Boolean).join(" · ")}
                  </p>
                )}
                {event?.name && <p className="text-sm">{event.name}</p>}
                {(event?.start_date || event?.end_date) && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <CalendarDays className="w-3.5 h-3.5" />
                    {formatCalendarRange(event.start_date, event.end_date)}
                  </p>
                )}
                {event?.venue_name && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {[event.venue_name, event.venue_city, event.venue_state]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                )}
              </div>
            </div>
          )}

          <DashboardButton onClick={goDashboard} loading={goingToDashboard} />
        </CardContent>
      </Card>
    </Shell>
  );
};

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-br from-background to-secondary/10 flex items-center justify-center p-4">
    {children}
  </div>
);

const DashboardButton: React.FC<{ onClick: () => void; loading: boolean }> = ({
  onClick,
  loading,
}) => (
  <Button className="w-full" onClick={onClick} disabled={loading}>
    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
    Go To My Dashboard
  </Button>
);

export default PurchaseReceiptPage;
